import { make } from "vuex-pathify"
import { GetterTree, ActionTree, MutationTree, Module } from "vuex"

import { RootState } from "@/store/RootStore"
import { StoreUtils } from "@/utils/StoreUtils"
import services from "@/services/Services"

import { Utils, DirectoryInfo } from "rp-components"

const namespaced: boolean = true

//===================================
// State
//===================================
export interface SupportState {
    rootDirectory?: DirectoryInfo
}

export const state: SupportState = {
    rootDirectory: undefined,
}

function searchAndUpdateMatchingDirectory(
    dir: DirectoryInfo,
    searchDir: DirectoryInfo,
    action: (oldDir: DirectoryInfo, newDir: DirectoryInfo) => void
) {
    if (dir.path === searchDir.path) {
        action(dir, searchDir)
        return
    } else {
        const subDirectories = dir.subDirectories ?? []
        for (const directory of subDirectories) {
            if (searchDir.path.startsWith(directory.path)) {
                searchAndUpdateMatchingDirectory(directory, searchDir, action)
            }
        }
    }
}

//===================================
// Getters
//===================================

export const getters: GetterTree<SupportState, RootState> = { }

//===================================
// Actions
//===================================
export const actions: ActionTree<SupportState, RootState> = {
    ...make.actions(state),

    async loadSupportDocuments({ dispatch, commit }, path: string) {
        await StoreUtils.withLoading(dispatch, async () => {
            const directoryInfo = await services.support.getSupportDocuments(path)
            if (path === "public/") {
                dispatch("setRootDirectory", directoryInfo)
            } else { 
                commit("updateDirectory", directoryInfo)
            }
        })
    },

    toggleDirectoryExpansion({ commit }, directory: DirectoryInfo) {
        commit("toggleDirectoryExpansion", directory)
    }
}

//===================================
// Mutators
//===================================
export const mutations: MutationTree<SupportState> = {
    ...make.mutations(state),

    updateDirectory(state, newDirectory: DirectoryInfo) {
        const rootDirectory = Utils.clone(state.rootDirectory)!

        searchAndUpdateMatchingDirectory(rootDirectory, newDirectory, (oldDir, newDir) => {
            oldDir.files = newDir.files
            oldDir.subDirectories = newDir.subDirectories
            oldDir.expanded = true
        })

        state.rootDirectory = rootDirectory
    },

    toggleDirectoryExpansion(state, directory: DirectoryInfo) { 
        const rootDirectory = Utils.clone(state.rootDirectory)!

        searchAndUpdateMatchingDirectory(rootDirectory, directory, (oldDir, newDir) => {
            oldDir.expanded = !newDir.expanded
        })

        state.rootDirectory = rootDirectory
    }
}

//===================================
// Module
//===================================
export const support: Module<SupportState, RootState> = {
    namespaced,
    state: state,
    getters: getters,
    actions: actions,
    mutations: mutations
}
